import {
  applySnapshot,
  cast,
  clone,
  flow,
  getSnapshot,
  IMSTArray,
  Instance,
  ISimpleType,
  types,
} from 'mobx-state-tree';
import { toJS } from 'mobx';
import { Requests } from '../../http-requests';
import { RequestStateModel } from './general-models';
import { prepareParams } from '../../utils';

export interface IGroupValidationErrors {
  name?: string;
  body?: string;
  addresses?: string;
}

const StatusesModel = types.model({
  saveExecutor: types.optional(RequestStateModel, {}),
  getDirectory: types.optional(RequestStateModel, {}),
  getContractors: types.optional(RequestStateModel, {}),
  getContractorById: types.optional(RequestStateModel, {}),
  createExecutor: types.optional(RequestStateModel, {}),
  deleteExecutor: types.optional(RequestStateModel, {}),
  createAlgorithm: types.optional(RequestStateModel, {}),
  saveAlgorithm: types.optional(RequestStateModel, {}),
  deleteAlgorithm: types.optional(RequestStateModel, {}),
  updateExecutorsOrder: types.optional(RequestStateModel, {}),
  getHint: types.optional(RequestStateModel, {}),
  setHint: types.optional(RequestStateModel, {}),
});

const ContractorModel = types.model({
  id: types.maybe(types.string),
  inn: types.optional(types.string, ''),
  str_name: types.optional(types.string, ''),
});

const GroupHouseBindingModel = types.model({
  house_groups: types.optional(types.array(types.string), []),
  houses: types.optional(types.array(types.string), []),
});

const GroupModel = types.model({
  body: types.optional(types.string, ''),
  contractor: types.maybe(ContractorModel),
  house_binding: types.optional(GroupHouseBindingModel, {}),
  id: types.maybe(types.string),
  name: types.optional(types.string, ''),
});

const ExecutorModel = types.model({
  algorithms: types.optional(types.array(GroupModel), []),
  description: types.optional(types.string, ''),
  provider_id: types.maybe(types.string),
  id: types.maybe(types.string),
  inn: types.optional(types.string, ''),
  position_id: types.maybeNull(types.number),
  str_name: types.optional(types.string, ''),
});

export const EditingGroupIdModel = types.model({
  isNew: types.boolean,
  id: types.string,
});

const CurrentViewModel = types.model({
  activeView: types.optional(
    types.union(types.literal('create'), types.literal('detail')),
    'detail'
  ),
});

const AllAddressesModel = types.model({
  active: types.optional(types.boolean, false),
  allHousesIdsFromFiasTree: types.optional(types.array(types.string), []),
});

export interface IExecutorModel extends Instance<typeof ExecutorModel> {}

export interface IGroupModel extends Instance<typeof GroupModel> {}

export interface IContractorModel extends Instance<typeof ContractorModel> {}

export interface IEditingGroupIdModel extends Instance<
  typeof EditingGroupIdModel
> {}

export const AlgoritmsDirectoryStore = types
  .model({
    statuses: types.optional(StatusesModel, {}),
    contractorsList: types.optional(types.array(ContractorModel), []),
    executorsList: types.optional(types.array(ExecutorModel), []),
    backupExecutorsList: types.optional(types.array(ExecutorModel), []),
    currentGroup: types.optional(GroupModel, {}),
    currentGroups: types.optional(types.array(GroupModel), []),
    currentExecutor: types.optional(ExecutorModel, {}),
    allHousesByExecutor: types.optional(
      types.map(types.array(types.string)), // key = executorId, value = массив всех айди домов
      {}
    ),
    newExecutor: types.optional(ExecutorModel, {}),
    editingGroupIds: types.optional(types.array(EditingGroupIdModel), []),
    currentView: types.optional(CurrentViewModel, {}),
    allAddresses: types.optional(AllAddressesModel, {}),
    triggeredRebuildHouseBinding: types.optional(types.boolean, false),
    groupEditingFormExist: types.optional(types.boolean, false),
    selectedGroupIndex: types.maybe(types.number),
    selectedTabIndex: types.maybe(types.number),
    hintVisible: types.optional(types.boolean, false),
    currentGroupsErrors: types.optional(
      types.array(types.frozen<IGroupValidationErrors>()),
      []
    ),
    globalValidationTriggered: types.optional(types.boolean, false),
    fiasTree: types.optional(types.frozen<{ [propName: string]: any }[]>(), []),
  })
  .views((self) => ({
    /*
      Вернет список id домов, привязанные к группе другим работником, отсутствующие в древе fias текущего работника
      (из-за отсутствия прав на них)
    */
    getUnavailableToSetGroupHouseBinding(
      untouchedGroup: IGroupModel
    ): string[] {
      return (
        untouchedGroup.house_binding?.houses.filter(
          (houseId) =>
            !self.allAddresses.allHousesIdsFromFiasTree.includes(houseId)
        ) || []
      );
    },
    /*
      Вернет список id групп домов, привязанные к группе другим работником, отсутствующие в houseGroups текущего работника
      (из-за отсутствия прав на них)
    */
    getUnavailableToSetGroupHouseGroupsBinding(
      fiasStorHouseGroups: any[],
      untouchedGroup: IGroupModel
    ): string[] {
      return untouchedGroup.house_binding!.house_groups.length
        ? untouchedGroup.house_binding!.house_groups.filter(
            (houseGroupId) =>
              !fiasStorHouseGroups.some(
                (groupObj) => groupObj.id === houseGroupId
              )
          )
        : [];
    },
    getDisabledItems(
      currentGroupId?: string,
      houseGroups: any[] = []
    ): { id: string; reason: string }[] {
      const items: { id: string; reason: string }[] = [];

      const addDisabled = (id: string, reason: string) => {
        if (!items.some((x) => x.id === id)) {
          items.push({ id, reason });
        }
      };

      const collect = (group: IGroupModel, isCurrent = false) => {
        if (isCurrent) return; // явно пропускаем текущую группу

        // дизейблим дома этой группы
        group.house_binding.houses.forEach((h) => {
          addDisabled(h, 'Адрес уже используется в другом алгоритме');

          // дизейблим группы, содержащие этот дом
          houseGroups
            .filter((g) => g.houses.includes(h))
            .forEach((g) =>
              addDisabled(g.id, 'Группа содержит уже выбранный дом')
            );
        });

        // дизейблим группы, указанные напрямую
        group.house_binding.house_groups.forEach((gId) => {
          addDisabled(gId, 'Группа уже используется в другом алгоритме');

          // дизейблим дома внутри этой группы
          const grp = houseGroups.find((hg) => hg.id === gId);
          grp?.houses.forEach((h: any) =>
            addDisabled(
              h,
              'Адрес входит в уже выбранную группу (в другом алгоритме)'
            )
          );
        });
      };

      // 💡 теперь явно передаём, какая группа текущая
      self.currentExecutor.algorithms.forEach((group) => {
        collect(group, group.id === currentGroupId);
      });

      self.currentGroups.forEach((group) => {
        collect(group, group.id === currentGroupId);
      });

      return items;
    },
    expandGroupHousesForBackend(algorithm: any, houseGroups: any) {
      const housesFromGroups = algorithm.house_binding.house_groups
        .map((groupId: string) =>
          houseGroups.find((g: any) => g.id === groupId)
        )
        .filter(Boolean)
        .flatMap((g: any) => g.houses);

      return {
        ...algorithm,
        house_binding: {
          ...algorithm.house_binding,
          houses: Array.from(
            new Set([...algorithm.house_binding.houses, ...housesFromGroups])
          ),
        },
      };
    },
    filterHousesFromBackend(algorithm: any, houseGroups: any) {
      const groupHouses = algorithm.house_binding.house_groups
        .map((groupId: string) =>
          houseGroups.find((g: any) => g.id === groupId)
        )
        .filter(Boolean)
        .flatMap((g: any) => g.houses);

      const groupHousesSet = new Set(groupHouses);

      return {
        ...algorithm,
        house_binding: {
          ...algorithm.house_binding,
          houses: algorithm.house_binding.houses.filter(
            (h: any) => !groupHousesSet.has(h)
          ),
        },
      };
    },
    getAlgorithmsByHouse(houseId: string): IExecutorModel[] {
      return self.executorsList
        .map((executor) => {
          const filteredAlgorithms = executor.algorithms.filter((alg) =>
            alg.house_binding.houses.includes(houseId)
          );

          if (filteredAlgorithms.length === 0) {
            return null;
          }

          return {
            ...executor,
            algorithms: filteredAlgorithms,
          } as IExecutorModel;
        })
        .filter(Boolean) as IExecutorModel[];
    },
    updateGroup(collection: IGroupModel[], algorithm: IGroupModel): void {
      const group = collection.find((g) => g.id === algorithm.id);

      if (group) {
        applySnapshot(group, algorithm);
      }
    },
    removeGroup(collection: IGroupModel[], id: string): void {
      const index = collection.findIndex((g) => g.id === id);

      if (index !== -1) {
        collection.splice(index, 1);
      }
    },
    algorithmsByHouse(houseId: string) {
      if (!self.currentExecutor) return [];

      return self.currentExecutor.algorithms.filter((alg) =>
        alg.house_binding.houses.includes(houseId)
      );
    },
    hasAnyHouseInGroups(currentGroupId: string): boolean {
      const hasInCurrentGroups = self.currentGroups.some(
        (group) =>
          group.id !== currentGroupId && group.house_binding.houses.length > 0
      );

      const hasInExecutorAlgorithms = self.currentExecutor.algorithms.some(
        (algorithm) =>
          algorithm.id !== currentGroupId &&
          algorithm.house_binding.houses.length > 0
      );

      return hasInCurrentGroups || hasInExecutorAlgorithms;
    },
    get isToggleDisabled(): boolean {
      // проверка по группам
      const hasSelectedInGroups = self.currentGroups.some(
        (group) =>
          (group.house_binding?.houses &&
            group.house_binding.houses.length > 0) ||
          (group.house_binding?.house_groups &&
            group.house_binding.house_groups.length > 0)
      );

      // проверка по алгоритмам текущего исполнителя
      const hasSelectedInExecutorAlgorithms =
        self.currentExecutor.algorithms.some(
          (algo) =>
            (algo.house_binding?.houses &&
              algo.house_binding.houses.length > 0) ||
            (algo.house_binding?.house_groups &&
              algo.house_binding.house_groups.length > 0)
        );

      return hasSelectedInGroups || hasSelectedInExecutorAlgorithms;
    },
  }))
  .volatile((self) => ({
    executorFormErrors: {} as Record<string, string>,
    executorFormTouched: false, // флаг для глобальной/локальной валидации
  }))
  .actions((self) => ({
    addAlgorithmsToNewExecutor(
      groupsPayload?: IGroupModel[],
      houseGroups?: any
    ) {
      if (!groupsPayload?.length) return;

      groupsPayload.forEach((group) => {
        const groupClone = { ...toJS(group) };
        const expanded = self.expandGroupHousesForBackend(
          groupClone,
          houseGroups
        );
        self.newExecutor.algorithms.push(expanded);
      });
    },
  }))
  .actions((self) => ({
    setExecutorFormTouched(touched: boolean) {
      self.executorFormTouched = touched;
    },
    setCurrentExecutor(list: IExecutorModel[], indexInList: number) {
      self.currentExecutor = {
        ...toJS(list[indexInList - 1]),
      };
    },
    setCurrentExecutorField(field: string, payload: string | IGroupModel[]) {
      self.currentExecutor = { ...self.currentExecutor, [field]: payload };
    },
    setNewExecutorField(field: string, payload: string | IGroupModel[]) {
      self.newExecutor = { ...self.newExecutor, [field]: payload };
    },
    setAllAddresses(
      active?: boolean,
      fiasTreeHouses?: IMSTArray<ISimpleType<string>>
    ) {
      if (typeof active === 'boolean') {
        self.allAddresses.active = active;
      }

      if (fiasTreeHouses) {
        self.allAddresses.allHousesIdsFromFiasTree = fiasTreeHouses;
      }
    },
    setGroupEditingFormExist(payload: boolean) {
      self.groupEditingFormExist = payload;
    },
    triggerRebuildHouseBinding(payload: boolean) {
      self.triggeredRebuildHouseBinding = payload;
    },
    addEmptyGroup() {
      if (self.currentGroups.length === 0) {
        self.currentGroups.push({
          id: crypto.randomUUID(),
          name: '',
          body: '',
          house_binding: { houses: [], house_groups: [] },
        });
      }
    },
    addCurrentGroup() {
      const newGroup = {
        id: crypto.randomUUID(),
        name: '',
        body: '',
        house_binding: { houses: [], house_groups: [] },
      };
      self.currentGroups.unshift(newGroup);
    },

    updateCurrentGroupField(index: number, field: string, payload: string) {
      const group = self.currentGroups[index];

      if (!group) {
        return;
      }
      const snapshot = getSnapshot(group);
      applySnapshot(group, {
        ...snapshot,
        [field]: payload,
      });
    },
    deleteGroupFromGroups(groupIndex: number) {
      self.currentGroups.splice(groupIndex, 1);
    },
    groupToEdit(groupIndex: number) {
      const currExecutorCopy = toJS(self.currentExecutor);
      self.currentGroup = {
        ...currExecutorCopy.algorithms.splice(groupIndex, 1)[0],
      };
    },
    setExecutorGroupsToCurrentGroups() {
      const currExecutorCopy = toJS(self.currentExecutor);
      self.currentGroups = cast(toJS(currExecutorCopy.algorithms));
    },
    setProviderGroupToCurrentGroups(groupId: string) {
      const excludedIds = (self.editingGroupIds || [])
        .filter((item) => item.isNew)
        .map((item) => item.id);
      const groupIndex = self.currentGroups.findIndex(
        (group) => group.id === groupId
      );
      const group =
        self.currentExecutor.algorithms[groupIndex - excludedIds.length];
      self.currentGroups[groupIndex] = cast(toJS(group));
    },
    removeGroupFromCurrenGroups(deletedId: string) {
      self.currentGroups.replace(
        self.currentGroups.filter((item) => item.id !== deletedId)
      );
    },
    addEditingGroupId(id: string, isNew: boolean) {
      const exists = self.editingGroupIds.some((item) => item.id === id);

      if (!exists) {
        self.editingGroupIds.push({ id, isNew });
      }
    },
    removeEditingGroupId(id: string) {
      self.editingGroupIds.replace(
        self.editingGroupIds.filter((item) => item.id !== id)
      );
    },
    clearEditingGroupIds() {
      self.editingGroupIds.clear();
    },
    shiftEditingGroupIds(deletedId: string) {
      self.editingGroupIds.replace(
        self.editingGroupIds.filter((item) => item.id !== deletedId)
      );
    },
    setCurrentView(tab: 'detail' | 'create') {
      self.currentView = {
        activeView: tab,
      };
    },
    clearCurrentExecutor() {
      applySnapshot(self.currentExecutor, {});
    },
    clearNewExecutor() {
      applySnapshot(self.newExecutor, {});
    },
    clearCurrentGroups() {
      applySnapshot(self.currentGroups, []);
    },
    reverseCurrentGroups() {
      self.currentGroups.replace([...self.currentGroups].reverse());
    },
    clearAlgoritmDirectoryStore() {
      applySnapshot(self.executorsList, []);
      applySnapshot(self.statuses, {});
      applySnapshot(self.currentGroup, {});
      applySnapshot(self.currentExecutor, {});
      applySnapshot(self.currentView, {});
    },
    getContractors: flow(function* () {
      self.statuses.getContractors.loadingStates();

      try {
        const { data } = yield Requests.GetAlgoritmsDirectoryContractors();

        self.contractorsList = data.map((contractor: any) => ({
          ...contractor,
          id: contractor._id,
        }));

        self.statuses.getContractors.successStates();
      } catch (e) {
        console.error(e);
        self.statuses.getContractors.errorStates();
      } finally {
        self.statuses.getContractors.finallyStates();
      }
    }),
    getContractorById: flow(function* (id: string) {
      const params = {
        provider_id: id,
      };

      self.statuses.getContractorById.loadingStates();

      try {
        const { data } =
          yield Requests.GetAlgoritmsDirectoryContractorById(params);

        self.statuses.getContractorById.successStates();
        return data;
      } catch (e) {
        console.error(e);
        self.statuses.getContractorById.errorStates();
      } finally {
        self.statuses.getContractorById.finallyStates();
      }
    }),
    getAlgoritmsDirectoryExecutors: flow(function* (houseGroups?: any[]) {
      self.statuses.getDirectory.loadingStates();

      try {
        const data = yield Requests.GetAlgoritmsDirectoryExecutors();
        // нормализуем все алгоритмы для фронта
        self.executorsList = data.map((executor: any) => {
          // сохраняем полный список домов для этого исполнителя
          self.allHousesByExecutor.set(
            executor.id,
            executor.algorithms.flatMap((alg: any) => alg.house_binding.houses)
          );

          return {
            ...executor,
            algorithms: executor.algorithms.map((alg: any) =>
              houseGroups ? self.filterHousesFromBackend(alg, houseGroups) : alg
            ),
          };
        });

        self.statuses.getDirectory.successStates();

        return self.executorsList;
      } catch (e) {
        console.error(
          'algorithmStore: promise getAlgoritmsDirectoryExecutors down'
        );
        self.statuses.getDirectory.errorStates();

        return Promise.reject(e);
      } finally {
        self.statuses.getDirectory.finallyStates();
      }
    }),
    // только для организации у которой нет вида деятельности "Управление домами" (вызывается единственный раз)
    createOwnAlgorithmsDirectory: flow(function* (payload: any) {
      const params = {
        contractor: { ...toJS(payload) },
      };

      self.statuses.createExecutor.loadingStates();

      try {
        const response =
          yield Requests.CreateAlgoritmsDirectoryExecutor(params);

        self.currentExecutor = {
          ...response.contractor,
        };

        self.executorsList.unshift(response.contractor);

        self.statuses.createExecutor.successStates();
      } catch (e) {
        console.error(e);
        self.statuses.createExecutor.errorStates();
      } finally {
        self.statuses.createExecutor.finallyStates();
      }
    }),
    createAlgorithmsDirectoryExecutor: flow(function* (
      groupsPayload?: IGroupModel[],
      houseGroups?
    ) {
      if (groupsPayload?.length) {
        self.addAlgorithmsToNewExecutor(groupsPayload, houseGroups);
      }

      const params = prepareParams(
        {
          contractor: { ...toJS(self.newExecutor) },
        },
        ['', undefined, null],
        ['houses', 'house_groups']
      );

      self.statuses.createExecutor.loadingStates();

      try {
        const response =
          yield Requests.CreateAlgoritmsDirectoryExecutor(params);

        self.currentExecutor = {
          ...response.contractor,
        };

        self.statuses.createExecutor.successStates();
      } catch (e) {
        console.error(e);
        self.statuses.createExecutor.errorStates();
      } finally {
        self.statuses.createExecutor.finallyStates();
      }
    }),
    saveAlgorithmsDirectoryExecutor: flow(function* (payload: string) {
      const params = {
        description: payload,
      };

      self.statuses.saveExecutor.loadingStates();

      try {
        const response = yield Requests.SaveAlgoritmsDirectoryExecutor(
          self.currentExecutor.id!,
          params
        );

        const executor = response.find(
          (exec: IExecutorModel) => exec.id === self.currentExecutor.id
        );

        self.currentExecutor = {
          ...executor,
        };

        const executorInList = self.executorsList.find(
          (exec) => exec.id === self.currentExecutor.id
        );

        if (executorInList) {
          applySnapshot(executorInList, executor);
        }

        self.statuses.saveExecutor.successStates();
      } catch (e) {
        console.error(e);
        self.statuses.saveExecutor.errorStates();
      } finally {
        self.statuses.saveExecutor.finallyStates();
      }
    }),
    // Нет смысла удалять исполнителя, тк он пересоздатся при появлении алгоритма в подрядной орг.
    // Код можно вернуть если продумают и расширят логику работы с алгоритмами*/}
    deleteAlgorithmsDirectoryExecutor: flow(function* (id: string) {
      self.statuses.deleteExecutor.loadingStates();

      try {
        yield Requests.DeleteAlgoritmsDirectoryExecutor(id);

        self.statuses.deleteExecutor.successStates();
      } catch (e) {
        console.error(e);
        self.statuses.deleteExecutor.errorStates();
      } finally {
        self.statuses.deleteExecutor.finallyStates();
      }
    }),
    createAlgorithm: flow(function* (payload?: any, houseGroups?: any[]) {
      self.statuses.createAlgorithm.loadingStates();

      try {
        // Добавляем contractor к payload
        const payloadWithContractor = {
          ...payload,
          contractor: self.currentExecutor?.id
            ? {
                id: self.currentExecutor.provider_id,
                inn: self.currentExecutor.inn,
                str_name: self.currentExecutor.str_name,
              }
            : undefined,
        };

        const expandedPayload = self.expandGroupHousesForBackend(
          payloadWithContractor,
          houseGroups
        );

        const response = yield Requests.CreateAlgorithm(expandedPayload);

        const algorithm = self.filterHousesFromBackend(response, houseGroups);

        self.currentGroups.push(algorithm);
        self.currentExecutor.algorithms.push(algorithm);
        const executorInList = self.executorsList.find(
          (executor) => executor.id === self.currentExecutor.id
        );

        if (executorInList) {
          executorInList.algorithms.push(algorithm);
        }

        self.statuses.createAlgorithm.successStates();
      } catch (e) {
        console.error(e);
        self.statuses.createAlgorithm.errorStates();
      } finally {
        self.statuses.createAlgorithm.finallyStates();
      }
    }),
    saveAlgorithm: flow(function* (payload?: any, houseGroups?: any) {
      self.statuses.saveAlgorithm.loadingStates();

      try {
        const formatPayload = self.expandGroupHousesForBackend(
          payload,
          houseGroups
        );

        const response = yield Requests.SaveAlgorithm(formatPayload);

        const algorithm = self.filterHousesFromBackend(
          response.group[0],
          houseGroups
        );

        self.updateGroup(self.currentGroups, algorithm);
        self.updateGroup(self.currentExecutor.algorithms, algorithm);

        const executorInList = self.executorsList.find(
          (executor) => executor.id === self.currentExecutor.id
        );

        if (executorInList) {
          self.updateGroup(executorInList.algorithms, algorithm);
        }

        self.statuses.saveAlgorithm.successStates();
      } catch (e) {
        console.error(e);
        self.statuses.saveAlgorithm.errorStates();
      } finally {
        self.statuses.saveAlgorithm.finallyStates();
      }
    }),
    deleteAlgorithm: flow(function* (id: string) {
      self.statuses.deleteAlgorithm.loadingStates();

      try {
        const response = yield Requests.DeleteAlgorithm(id);

        self.removeGroup(self.currentGroups, id);
        self.removeGroup(self.currentExecutor.algorithms, id);

        const executorInList = self.executorsList.find(
          (executor) => executor.id === self.currentExecutor.id
        );

        if (executorInList) {
          self.removeGroup(executorInList.algorithms, id);
        }

        self.statuses.deleteAlgorithm.successStates();
      } catch (e) {
        console.error(e);
        self.statuses.deleteAlgorithm.errorStates();
        throw e;
      } finally {
        self.statuses.deleteAlgorithm.finallyStates();
      }
    }),
    setSelectedGroupIndex(index: number) {
      self.selectedGroupIndex = index;
    },
    clearSelectedGroupIndex() {
      self.selectedGroupIndex = undefined;
    },
    setSelectedTabIndex(index: number) {
      self.selectedTabIndex = index;
    },
    clearSelectedTabIndex() {
      self.selectedTabIndex = undefined;
    },
    getHint: flow(function* () {
      self.statuses.getHint.loadingStates();

      try {
        const data = yield Requests.GetAlgorithmHint();
        self.hintVisible = data.hint;

        self.statuses.getHint.successStates();
      } catch (e) {
        console.error(e);
        self.statuses.getHint.errorStates();
      } finally {
        self.statuses.getHint.finallyStates();
      }
    }),
    setHintHidden: flow(function* () {
      self.statuses.setHint.loadingStates();

      try {
        const data = yield Requests.PostAlgorithmHint();
        self.hintVisible = data.hint;

        self.statuses.setHint.successStates();
      } catch (e) {
        console.error(e);
        self.statuses.setHint.errorStates();
      } finally {
        self.statuses.setHint.finallyStates();
      }
    }),
    reorderExecutors(fromIndex: number, toIndex: number) {
      const list = [...self.executorsList];

      if (toIndex < 0 || toIndex >= list.length) return;

      [list[fromIndex], list[toIndex]] = [list[toIndex], list[fromIndex]];

      list.forEach((executor, i) => {
        executor.position_id = i + 1;
      });

      self.executorsList.replace(list);
    },
    updateExecutorsOrder: flow(function* () {
      self.statuses.updateExecutorsOrder.loadingStates();

      try {
        const payload = {
          contractors: self.executorsList.map((executor) => ({
            id: executor.id,
            position_id: executor.position_id,
          })),
        };

        const response = yield Requests.UpdateExecutorsOrder(payload);

        self.executorsList = response;

        self.statuses.updateExecutorsOrder.successStates();
      } catch (e) {
        console.error(e);
        self.statuses.updateExecutorsOrder.errorStates();
      } finally {
        self.statuses.updateExecutorsOrder.finallyStates();
      }
    }),
    saveExecutorsBackup() {
      const backup = self.executorsList.map((executor) => clone(executor));
      self.backupExecutorsList.replace(backup);
    },
    restoreExecutorsBackup() {
      const original = self.backupExecutorsList.map((executor) =>
        clone(executor)
      );
      self.executorsList.replace(original);
    },
    setHintVisibility(payload: boolean) {
      self.hintVisible = payload;
    },
    // управление валидацией форм
    triggerGlobalValidation() {
      self.globalValidationTriggered = true;
    },
    resetGlobalValidation() {
      self.globalValidationTriggered = false;
      self.currentGroupsErrors.clear();
    },
    setGroupErrors(groupIndex: number, errors: IGroupValidationErrors) {
      self.currentGroupsErrors[groupIndex] = errors;
    },
    clearErrors() {
      self.currentGroupsErrors.clear();
    },
    removeGroupError(index: number) {
      self.currentGroupsErrors.splice(index, 1);
    },
    triggerExecutorFormValidation(errors: Record<string, string>) {
      self.executorFormErrors = errors;
    },
    clearExecutorFormValidation() {
      self.executorFormErrors = {};
    },
    hasExecutorFormErrors() {
      return Object.keys(self.executorFormErrors).length > 0;
    },
    setFiasTree(tree: { [propName: string]: any }[]) {
      self.fiasTree = tree;
    },
    clearFiasTree() {
      self.fiasTree = [];
    },
  }));
